import { useEffect, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "../ui/modal.tsx";
import { Button } from "../ui/button.tsx";
import type { Announcement } from "../../api/announcements.ts";
import { formatFileSize } from "../../util/announcementPdf.ts";
import { StatusIcon } from "../ui/statusIcon.tsx";
import { AnnouncementPdfViewer } from "./announcementPdfViewer.tsx";
import styles from "./announcement.module.scss";

interface Props {
  announcement: Announcement | null;
  onClose: () => void;
  /** Marks it as read. Omit for a preview that records nothing (the SuperAdmin tool's "Preview"). */
  onMarkRead?: () => void;
  marking?: boolean;
  /** A PDF chosen but not uploaded yet (the SuperAdmin preview): shown from the browser instead of fetched. */
  previewFile?: Blob | null;
}

/**
 * The full text of an announcement, with its PDF readable right in the dialog. Reading is confirmed, not assumed: the receipt
 * is written by "Mark as read".
 */
export function AnnouncementModal({ announcement, onClose, onMarkRead, marking = false, previewFile = null }: Props) {
  const a = announcement;
  // The dialog widens while the PDF is on screen.
  const [viewing, setViewing] = useState(false);
  useEffect(() => { if (!a) setViewing(false); }, [a]);

  // A sample without an id (the modal gallery) has nothing to fetch, so it only names the file.
  const canView = a !== null && (a.AnnouncementId > 0 || previewFile !== null);

  return (
    <Modal open={a !== null} onClose={onClose} wide={viewing}>
      <ModalHeader title={a?.Title ?? ""} onClose={onClose} />
      <ModalBody>
        {a && (
          <div>
            <span className={styles.modalEyebrow}>News</span>
            <p className={styles.message}>{a.Body}</p>
            {a.AttachmentName && (
              <div className={styles.linkRow}>
                {canView ? (
                  <AnnouncementPdfViewer
                    key={`${a.AnnouncementId}|${a.AttachmentName}`}
                    announcementId={a.AnnouncementId}
                    fileName={a.AttachmentName}
                    fileSize={a.AttachmentSize}
                    localFile={previewFile}
                    onViewingChange={setViewing}
                  />
                ) : (
                  <span className={styles.pdfChip}>
                    <StatusIcon kind="file" size={16} />
                    <span className={styles.pdfName}>{a.AttachmentName}</span>
                    <span className={styles.pdfSize}>{formatFileSize(a.AttachmentSize)}</span>
                  </span>
                )}
              </div>
            )}
            {onMarkRead && (
              <p className={styles.hint}>Marking it as read records your name, so we can see who has read it.</p>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={marking}>
          {onMarkRead ? "Later" : "Close"}
        </Button>
        {onMarkRead && (
          <Button variant="primary" onClick={onMarkRead} loading={marking}>
            Mark as read
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}
